// client/src/services/askkeith.ts
// API client for the AskKeith tax research chatbot.

export type Confidence = 'high' | 'medium' | 'low' | 'none' | 'info';

export type AskKeithRef = {
  id: string;                  // 'sN' — matches [^sN] markers in the answer
  guid: string;
  version: number;
  chunk_ordinal: number;
  chunk_type: 'body' | 'summary' | 'footnote';
  headline: string | null;
  document_type: string | null;
  citation: string | null;
  rid: string | null;
  publication_current_date: string | null;
  publication_initial_date: string | null;
  content_modified_at: string | null;
  score: number;
};

export type AskKeithSessionSummary = {
  session_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  message_count: number;
};

export type AskKeithMessage = {
  message_id: number;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  confidence_level: Confidence | null;
  confidence_score: number | null;
  citation_count: number | null;
  retrieval_snapshot: {
    refs?: AskKeithRef[];
    topScore?: number;
    avgScore?: number;
    strongCount?: number;
    initialConfidence?: Confidence;
  } | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost_usd: number | null;
  model: string | null;
};

export type AskKeithAskResponse = {
  sessionId: string;
  title: string | null;
  isNewSession: boolean;
  messageId?: number;
  assistantMessage: string;
  refs: AskKeithRef[];
  confidence: Confidence;
  topScore: number;
  citationCount?: number;
  toolsUsed?: string[];
  usage: {
    inputTokens: number;
    outputTokens: number;
    cacheReadInputTokens?: number;
    cacheCreationInputTokens?: number;
    toolIterations?: number;
    model: string;
    estimatedCostUsd: number;
  } | null;
  elapsedMs: number;
};

// ─────────────────────────────────────────────────────────────────────────────

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', cache: 'no-store', ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Server error ${res.status}`);
  }
  return res.json();
}

function post<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function askKeith(params: { sessionId?: string; message: string }): Promise<AskKeithAskResponse> {
  return post('/api/askkeith-ask', params);
}

export function listAskKeithSessions(limit = 50): Promise<{ sessions: AskKeithSessionSummary[] }> {
  return api(`/api/askkeith-sessions?limit=${limit}`);
}

export function loadAskKeithSession(sessionId: string): Promise<{
  session: AskKeithSessionSummary;
  messages: AskKeithMessage[];
}> {
  return api(`/api/askkeith-session/${encodeURIComponent(sessionId)}`);
}

export function renameAskKeithSession(sessionId: string, title: string): Promise<{ ok: true }> {
  return post(`/api/askkeith-session/${encodeURIComponent(sessionId)}`, { title });
}

export function deleteAskKeithSession(sessionId: string): Promise<{ ok: true }> {
  return api(`/api/askkeith-session/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}
